'use client';

import { getAuthorList } from '@/api/blog';
import { IAdvertising, IAuthorListItem, IBlogConfig } from '@/api/types/blog';
import AuthorCard from '@/app/blog/components/AuthorCard';
import AuthorNavList from '@/app/blog/components/AuthorNavList';
import Breadcrumbs from '@/components/Breadcrumbs';
import InfiniteScroll from '@/components/InfiniteScroll';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useBlogAuthorsTrack } from '../hooks/blogAuthorsTrack';

interface AuthorsClientProps {
  initialAuthorList: {
    data: IAuthorListItem[];
    total: number;
  };
  initialAdvertising: IAdvertising[];
  navList: IBlogConfig[];
  limit?: number;
}

export default function AuthorsClient({
  initialAuthorList,
  initialAdvertising,
  navList,
  limit = 12,
}: AuthorsClientProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;

  // 页面状态管理
  const [authors, setAuthors] = useState<IAuthorListItem[]>(initialAuthorList.data || []);
  const [total, setTotal] = useState(initialAuthorList.total || 0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const { sdBlogAuthorListPageAuthorExp, sdBlogAuthorListPageAuthorClick } = useBlogAuthorsTrack();

  const listTitleInfo = useMemo(() => {
    const data =
      Array.isArray(initialAdvertising) && initialAdvertising.length > 0
        ? initialAdvertising[0].content
        : null;
    return data;
  }, [initialAdvertising]);

  const fetchAuthors = useCallback(
    async (pageNum: number) => {
      try {
        setLoading(true);
        const data: any = await getAuthorList({ page: pageNum, limit });
        const { data: { author_list: { data: listData = [], total: newTotal } = {} } = {}, code } =
          data;
        if (code === 200) {
          // 追加数据（无限滚动）
          setAuthors((prevAuthors) => [...prevAuthors, ...listData]);
          setTotal(newTotal);
        }
      } catch (e) {
        logger.log(e);
      } finally {
        setLoading(false);
      }
    },
    [limit],
  );

  // 处理滚动到底部
  const handleReachBottom = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchAuthors(nextPage);
  }, [page, fetchAuthors]);

  return (
    <div className={`bg-[#f4f4f4] ${isWeb ? 'min-h-[40vh]' : 'min-h-screen'}`}>
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'ZEELOOL Blog', href: '/blog' },
          { label: 'Authors', href: '/blog/authors' },
        ]}
        className={`${isWeb ? 'pl-48 pt-12' : 'pl-14 pt-10'}`}
        sx={{
          '& .MuiBreadcrumbs-li p, & .MuiBreadcrumbs-li a span, & .MuiBreadcrumbs-separator': {
            fontSize: '12px',
            color: colors.primaryBlack,
          },
          '& .MuiBreadcrumbs-li a': {
            display: 'block',
            fontSize: '12px',
            lineHeight: '1.5',
          },
        }}
      />
      {listTitleInfo && (
        <div className={`${isWeb ? 'pt-40 pb-32 w-[800px] mx-auto' : 'px-14 py-32'}`}>
          <h1
            className={`font-bb whitespace-pre-line ${isWeb ? 'text-[40px] leading-[60px]' : 'text-20 leading-34'}`}
          >
            {listTitleInfo.title}
          </h1>
          <h5
            className={`font-ir whitespace-pre-line ${isWeb ? 'mt-12 text-20 leading-30' : 'mt-16 text-[13px] leading-20'}`}
          >
            {listTitleInfo.description}
          </h5>
        </div>
      )}
      <div
        className={`${isWeb ? 'pt-32 pb-24 mx-auto grid grid-cols-2 auto-rows-stretch gap-[24px] [@media(max-width:1279px)]:grid-cols-1 w-[78vw] min-w-[1184px]' : 'px-14'}`}
      >
        {authors.map((author, index) => (
          <div key={author.id} className={`${isWeb ? 'bg-white' : 'mb-12 last:mb-0'}`}>
            <AuthorCard
              author={author}
              index={index}
              onAuthorClick={sdBlogAuthorListPageAuthorClick}
              onExposure={sdBlogAuthorListPageAuthorExp}
              isWeb={isWeb}
            />
          </div>
        ))}
      </div>
      <InfiniteScroll
        loading={loading}
        onReachBottom={handleReachBottom}
        page={page}
        total={total}
        limit={limit}
      />
      {authors.length === total && <AuthorNavList navList={navList} isWeb={isWeb} />}
    </div>
  );
}
